import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { PageLoader } from "@/components";
import {
  TEMPLATE_CALENDAR,
  TEMPLATE_PLAYLISTS,
  TEMPLATE_SEARCH,
} from "@/const.ts";
import { useAppProperties, useCurrentTabId, useMainTabs } from "@/hooks";
import { useResponsiveness, useTranslate } from "@/providers/context";
import { cn, getUrlFromFilename } from "@/utils";

import { NavLink } from "./NavLink";
import { NavTab } from "./NavTab";

const Divider = () => {
  return <div className="h-[1px] w-full shrink-0 bg-border" />;
};

export const NavBar = () => {
  const { tabId } = useCurrentTabId();
  const { pathname } = useLocation();
  const { mainTabs, sortedClickableMainTabs, communityTab } = useMainTabs();
  const navigate = useNavigate();
  const {
    data: properties,
    favouritesLabel,
    watchlistLabel,
    showFavourites,
    showWatchlist,
    externalLinks,
    communityEnabled,
  } = useAppProperties();
  const { condensedNavBar } = useResponsiveness();
  const { translate } = useTranslate();

  const squareImage = properties?.SideMenuHeaderSquareWeb
    ? properties.SideMenuHeaderSquareWeb
    : properties?.AppIconImage;
  const navBarImage = mainTabs
    ? mainTabs[0]?.Properties?.NavBarTitleImage
    : undefined;
  const landscapeImage = properties?.SideMenuHeaderLandscapeWeb
    ? properties.SideMenuHeaderLandscapeWeb
    : navBarImage;
  const headerTitleImage = condensedNavBar ? squareImage : landscapeImage;
  const headerLogoUrl = headerTitleImage
    ? getUrlFromFilename(headerTitleImage)
    : "";

  const {
    existingSearchTab,
    existingCalendarTab,
    existingPlaylistTab,
    existingCommunityTab,
  } = useMemo(() => {
    const existingSearchTab = mainTabs?.find(
      (t) => t.TemplateId === TEMPLATE_SEARCH,
    );
    const existingCalendarTab = mainTabs?.find(
      (t) => t.TemplateId === TEMPLATE_CALENDAR,
    );
    const existingPlaylistTab = mainTabs?.find(
      (t) => t.TemplateId === TEMPLATE_PLAYLISTS,
    );
    const existingCommunityTab = communityEnabled ? communityTab : undefined;
    return {
      existingSearchTab,
      existingCalendarTab,
      existingPlaylistTab,
      existingCommunityTab,
    };
  }, [mainTabs, communityTab, communityEnabled]);

  return (
    <div
      data-testid="sidebar"
      className={cn([
        "relative flex h-full flex-shrink-0 flex-col border-r-[1px] border-border pt-7 text-[16px] font-semibold text-item-primary",
        condensedNavBar ? "w-[88px]" : "w-[250px]",
      ])}
    >
      {!mainTabs ? (
        <PageLoader />
      ) : (
        <>
          <div
            className="mb-[28px] flex h-[38px] w-full cursor-pointer items-center justify-center gap-[16px] rounded-[6px]"
            onClick={() => navigate("/")}
          >
            <img
              alt="app logo"
              src={headerLogoUrl}
              className={cn([
                "h-[38px] rounded",
                condensedNavBar ? "w-[38px]" : "w-[177px]",
              ])}
            />
          </div>
          <div className="flex flex-col gap-[12px] overflow-y-auto">
            {sortedClickableMainTabs?.map((t, idx) => {
              const active =
                tabId === t.TabId?.toString() ||
                (idx === 0 && pathname === "/");
              return (
                <NavTab
                  key={t.TabId}
                  active={active}
                  home={idx === 0}
                  tabId={t.TabId}
                  icon={t.Icon}
                  name={t.Name}
                  data-testid={`tab-button-${idx}`}
                />
              );
            })}
            <NavTab
              active={pathname === "/search"}
              tabId="search"
              name={existingSearchTab?.Name ?? translate("Search")}
              icon={existingSearchTab?.Icon ?? "icon_search_2022.png"}
            />
            {existingCalendarTab && (
              <NavTab
                active={pathname === "/calendar"}
                tabId="calendar"
                name={existingCalendarTab?.Name}
                icon={existingCalendarTab?.Icon ?? "icon_calendar_2022.png"}
              />
            )}
            {existingCommunityTab && (
              <NavTab
                active={pathname.startsWith("/community")}
                tabId="community"
                name={existingCommunityTab?.Name ?? translate("Community")}
                icon={existingCommunityTab?.Icon}
              />
            )}
            <Divider />
            {showFavourites && (
              <NavTab
                active={pathname === "/favorites"}
                tabId="favorites"
                name={favouritesLabel}
                icon="favourites.png"
              />
            )}
            {showWatchlist && (
              <NavTab
                active={pathname === "/watch"}
                tabId="watch"
                name={watchlistLabel}
                icon="watchlist.png"
              />
            )}
            <NavTab
              active={pathname === "/recent"}
              tabId="recent"
              name={translate("Recently Watched")}
              icon="recent.png"
            />
            {existingPlaylistTab && (
              <NavTab
                key={`playlists`}
                active={pathname === `/playlists`}
                tabId={`playlists`}
                name={existingPlaylistTab.Name}
                icon={existingPlaylistTab?.Icon ?? "icon_playlist.png"}
              />
            )}
            {externalLinks.length > 0 && (
              <>
                <Divider />
                {externalLinks.map((link) => (
                  <NavLink
                    key={link.id}
                    title={link.title}
                    url={link.url}
                    icon={link.iconUrl}
                  />
                ))}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};
